/**
 * Утилита для отложенного выполнения callback после анимаций
 * Позволяет дождаться ripple эффекта или других анимаций перед тяжелыми операциями
 */

export interface DelayedExecutionOptions {
  /**
   * Минимальная задержка перед выполнением (ms)
   * Гарантирует что анимация успеет начаться
   * @default 100
   */
  minDelay?: number

  /**
   * Максимальная задержка перед выполнением (ms)
   * Защита от слишком долгого ожидания
   * @default 300
   */
  maxDelay?: number

  /**
   * Использовать requestIdleCallback если доступен
   * Идеально для тяжелых операций
   * @default true
   */
  useIdleCallback?: boolean

  /**
   * Количество requestAnimationFrame перед выполнением
   * 1 - анимация началась
   * 2 - анимация видна пользователю (рекомендуется)
   * 3+ - для очень медленных устройств
   * @default 2
   */
  rafCount?: number

  /**
   * Приоритет выполнения для requestIdleCallback
   * 'background' - низкий приоритет, выполнится когда браузер свободен
   * 'user-visible' - средний приоритет
   * 'user-blocking' - высокий приоритет (не рекомендуется)
   * @default 'user-visible'
   */
  priority?: 'background' | 'user-visible' | 'user-blocking'
}

/**
 * Выполняет callback с задержкой для анимаций
 *
 * @param callback - Функция для выполнения
 * @param options - Опции задержки
 *
 * @example
 * ```typescript
 * // Базовое использование
 * executeAfterAnimation(() => {
 *   router.push('/catalog')
 * })
 *
 * // С кастомными настройками
 * executeAfterAnimation(() => {
 *   heavyComputation()
 * }, {
 *   minDelay: 150,
 *   maxDelay: 500,
 *   rafCount: 3,
 *   priority: 'background'
 * })
 * ```
 */
export function executeAfterAnimation(
  callback: () => void,
  options: DelayedExecutionOptions = {}
): void {
  const {
    minDelay = 100,
    maxDelay = 200,
    useIdleCallback = true,
    rafCount = 2,
    priority = 'user-visible',
  } = options

  // Рекурсивная функция для множественных RAF
  const scheduleRAF = (remaining: number, finalCallback: () => void) => {
    if (remaining <= 0) {
      finalCallback()
      return
    }

    requestAnimationFrame(() => {
      scheduleRAF(remaining - 1, finalCallback)
    })
  }

  // Выполняем RAF цепочку
  scheduleRAF(rafCount, () => {
    // После RAF выбираем стратегию выполнения
    if (useIdleCallback && 'requestIdleCallback' in window) {
      // Используем requestIdleCallback для тяжелых операций
      let timeout: number

      switch (priority) {
        case 'background':
          timeout = maxDelay * 2
          break
        case 'user-visible':
          timeout = maxDelay
          break
        case 'user-blocking':
          timeout = Math.min(maxDelay, 100) // Максимум 100ms для блокирующих
          break
        default:
          timeout = maxDelay
      }

      requestIdleCallback(callback, { timeout })
    } else {
      // Fallback на setTimeout
      let delay: number

      switch (priority) {
        case 'background':
          delay = maxDelay
          break
        case 'user-visible':
          delay = minDelay
          break
        case 'user-blocking':
          delay = Math.min(minDelay, 50) // Максимум 50ms для блокирующих
          break
        default:
          delay = minDelay
      }

      setTimeout(callback, delay)
    }
  })
}
